import { useCallback } from 'react';
import TagManager from 'react-gtm-module';
import { captureException } from '@sentry/react';
import { useAppSelector } from '@/app/hooks';
import { useUserClientContext } from '@/app/clientContext';
import {
  getSuggestions,
  type Suggestion,
  type SuggestionsByType,
} from '@/features/searchSlice';
import {
  useLazyRetrieveQuery,
  useLazySuggestQuery,
} from '@/features/searchMapboxSlice';
import { GoogleEvents } from '@/features/googleEvents';
import { ADDRESS_PATH } from '@/app/constants';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { parseCoordinatePair } from '@/helpers/geo';
import { DEFAULT_FALLBACK_ZOOM_LEVEL } from '@/views/map/deckglUtils';
import { buildViewStateSearchParams } from '@/views/map/useViewStateUrlSync';

const buildAddressLink = (coords: { lat: number; lng: number }) =>
  `${ADDRESS_PATH}?${buildViewStateSearchParams({
    lat: coords.lat,
    lng: coords.lng,
    zoom: DEFAULT_FALLBACK_ZOOM_LEVEL,
  }).toString()}`;

/** Span/pole/internal-id results from the navigator search API. The cache key
 * (`changeCount` in `filterQuery`) bumps whenever filter state changes, which
 * forces the consumer's cache to wipe — so this hook itself doesn't need to
 * cache. */
export const useAppSuggester = () => {
  const filterQuery = useAppSelector((state) => state.filterQuery);
  const { selectedAnalysis } = useAppSelector((s) => s.analysisMap);
  const { token, apiIdentity } = useUserClientContext();

  return useCallback(
    (term: string): Promise<SuggestionsByType> =>
      getSuggestions(term, token, apiIdentity, filterQuery, selectedAnalysis),
    [token, apiIdentity, filterQuery, selectedAnalysis]
  );
};

export const useMapboxSuggester = ({
  mapCenter,
  bbox,
}: {
  mapCenter: { lat: number; lng: number };
  bbox?: number[];
}) => {
  const [suggest] = useLazySuggestQuery();
  const [retrieve] = useLazyRetrieveQuery();
  const navigate = useNavigateWithSearch();

  const { lat, lng } = mapCenter;

  return useCallback(
    async (term: string): Promise<Suggestion[]> => {
      // Skip the round-trip if the user typed a coordinate pair — Mapbox
      // throws on coordinate input, and the coordinate suggester handles it.
      if (parseCoordinatePair(term)) return [];

      try {
        const raw = await suggest({
          addressQuery: term,
          coords: { lat, lng },
          bbox,
          limit: 3,
        }).unwrap();

        return raw.map(({ value, subtitle, mapboxId }) => ({
          kind: 'callback',
          value,
          subtitle,
          onSelect: async () => {
            TagManager.dataLayer({
              dataLayer: { event: GoogleEvents.searchByAddress },
            });
            try {
              const { data: coords } = await retrieve({ mapboxId });
              if (!coords) return;
              navigate(buildAddressLink(coords), { sessionOnly: true });
            } catch (error) {
              captureException(error, {
                extra: { source: 'mapbox-retrieve' },
              });
            }
          },
        }));
      } catch (error) {
        captureException(error, { extra: { source: 'mapbox-suggest' } });
        return [];
      }
    },
    [suggest, retrieve, navigate, lat, lng, bbox]
  );
};

export const coordinateSuggester = (term: string): Suggestion[] => {
  const coords = parseCoordinatePair(term);
  if (!coords) return [];
  return [
    {
      kind: 'link',
      value: term,
      subtitle: 'Coord',
      href: buildAddressLink(coords),
    },
  ];
};
